import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminTableHeader,
  AdminTableBody,
  AdminSupportingText,
  AdminHeaderAction,
  AdminRowActionsButton,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  ARTICLE_USE_OPTIONS,
  CATEGORY_OPTIONS,
  KB_ROUTE,
  KB_SUPPORTING_TEXT,
  buildSampleKnowledgeArticles,
  matchesKnowledgeSearch,
} from '../../../data/knowledgeBaseSpec';

/** Article ID · Title · Category · Article Use · Actions */
const gridTemplateColumns =
  'minmax(110px, 1fr) minmax(320px, 3.2fr) minmax(180px, 1.5fr) minmax(170px, 1.4fr) minmax(52px, 52px)';

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [articles, setArticles] = useState(() => {
    const incoming = location.state?.articles;
    return Array.isArray(incoming) ? incoming : buildSampleKnowledgeArticles();
  });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ category: null, articleUse: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [kbRowActionsOpen, setKbRowActionsOpen] = useState({});
  const kbRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);

  const {
    layout: kbRowActionsMenuLayout,
    menuRef: kbRowActionsMenuRef,
    placementReady: kbRowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    kbRowActionsOpen,
    kbRowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.DOUBLE
  );

  useEffect(() => {
    const incoming = location.state?.articles;
    const feedback = location.state?.feedback;
    if (Array.isArray(incoming) || feedback) {
      if (Array.isArray(incoming)) setArticles(incoming);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const toggleKbRowActions = (rowKey) => {
    setKbRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(kbRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setKbRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryRef = useRef(null);
  const articleUseRef = useRef(null);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [articleUseOpen, setArticleUseOpen] = useState(false);

  useClickOutside(categoryRef, () => setCategoryOpen(false));
  useClickOutside(articleUseRef, () => setArticleUseOpen(false));

  const activeArticles = useMemo(
    () => articles.filter((article) => !article.archived),
    [articles]
  );

  const filteredArticles = useMemo(() => {
    return activeArticles.filter((article) => {
      const matchesCategory = !filters.category || article.category === filters.category;
      const matchesArticleUse =
        !filters.articleUse || article.articleUse === filters.articleUse;
      return (
        matchesCategory &&
        matchesArticleUse &&
        matchesKnowledgeSearch(article, search)
      );
    });
  }, [activeArticles, filters, search]);

  const totalCount = filteredArticles.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(filteredArticles.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [filteredArticles.length]);

  const paginatedArticles = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return filteredArticles.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [filteredArticles, effectivePage]);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const openArticleView = (article) => {
    navigate(`${KB_ROUTE}/${encodeURIComponent(article.articleId)}/view`, {
      state: { from: location.pathname, article, articles },
    });
  };

  const openArticleEdit = (article) => {
    navigate(`${KB_ROUTE}/${encodeURIComponent(article.articleId)}/edit`, {
      state: { from: location.pathname, article, articles },
    });
  };

  return (
    <AdminLayout>
      <div>
        <div
          className={`flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <div className="flex-1 min-w-0">
            <AdminPageTitle className="!mb-1">Knowledge Base</AdminPageTitle>
            <AdminSupportingText className="text-mediumgray mb-0">
              {KB_SUPPORTING_TEXT}
            </AdminSupportingText>
          </div>
          <AdminHeaderAction
            onClick={() =>
              navigate(`${KB_ROUTE}/add`, {
                state: { from: location.pathname, articles },
              })
            }
          >
            Add Article
          </AdminHeaderAction>
        </div>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="w-full max-w-[450px]">
              <AdminSearchField
                placeholder="Search Title, Keyword, or Topic..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Search knowledge base"
              />
            </div>

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Category"
                value={filters.category}
                options={[
                  { value: null, label: 'All' },
                  ...CATEGORY_OPTIONS.map((category) => ({
                    value: category,
                    label: category,
                  })),
                ]}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, category: value }));
                  setCurrentPage(1);
                }}
                menuRef={categoryRef}
                open={categoryOpen}
                setOpen={setCategoryOpen}
              />
              <AdminListFilterDropdown
                label="Article Use"
                value={filters.articleUse}
                options={[
                  { value: null, label: 'All' },
                  ...ARTICLE_USE_OPTIONS.map((use) => ({ value: use, label: use })),
                ]}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, articleUse: value }));
                  setCurrentPage(1);
                }}
                menuRef={articleUseRef}
                open={articleUseOpen}
                setOpen={setArticleUseOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {paginatedArticles.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No articles match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <div ref={tableScrollRef} className="overflow-x-auto">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4 min-w-[960px]"
                style={{ gridTemplateColumns }}
              >
                <AdminTableHeader>Article ID</AdminTableHeader>
                <AdminTableHeader>Title</AdminTableHeader>
                <AdminTableHeader>Category</AdminTableHeader>
                <AdminTableHeader>Article Use</AdminTableHeader>
                <AdminTableHeader className="text-center">Actions</AdminTableHeader>
              </div>
              {paginatedArticles.map((article) => (
                <div
                  key={article.articleId}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer min-w-[960px]"
                  style={{ gridTemplateColumns }}
                  onClick={() => openArticleView(article)}
                >
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate">{article.articleId}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate" title={article.title}>
                      {article.title}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate">{article.category}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableBody className="truncate">{article.articleUse}</AdminTableBody>
                  </div>
                  <div
                    className="flex items-center justify-center relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        kbRowActionsRefs.current[article.articleId] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleKbRowActions(article.articleId);
                        }}
                        title="Actions"
                        aria-label={`Open actions for ${article.articleId}`}
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {kbRowActionsOpen[article.articleId] && kbRowActionsMenuLayout ? (
                        <AdminFixedRowActionsMenu
                          layout={kbRowActionsMenuLayout}
                          menuRef={kbRowActionsMenuRef}
                          placementReady={kbRowActionsMenuPlacementReady}
                        >
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openArticleView(article);
                              setKbRowActionsOpen({});
                            }}
                          >
                            View
                          </AdminRowMenuItem>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openArticleEdit(article);
                              setKbRowActionsOpen({});
                            }}
                          >
                            Edit
                          </AdminRowMenuItem>
                        </AdminFixedRowActionsMenu>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default KnowledgeBase;
